import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { DataProvider } from './contexts/DataContext';
import { AuthProvider } from './contexts/AuthContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { LayoutProvider } from './contexts/LayoutContext';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import { 
  Container, 
  Box, 
  Drawer, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Typography,
  Divider,
  IconButton,
  useMediaQuery
} from '@mui/material';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import InsightsIcon from '@mui/icons-material/Insights';
import SavingsIcon from '@mui/icons-material/Savings';
import RuleFolderIcon from '@mui/icons-material/RuleFolder';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import './App.css';
import BudgetCluesLogo from './components/BudgetCluesLogo';

import MainDashboard from './components/Dashboard/Dashboard';
import CsvImportTool from './components/Transactions/CsvImportTool';
import BudgetView from './components/Budget/BudgetView';
import Budgets from './components/Budgets/Budgets';
import RulesManager from './components/Rules/RulesManager';
import TransactionList from './components/Transactions/TransactionList';
import CategoryManager from './components/Categories/CategoryManager';
import AccountManager from './components/Accounts/AccountManager';
import ActionManager from './components/Actions/ActionManager';
import GoalList from './components/Goals/GoalList';
import Login from './components/Auth/Login';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { useAuth } from './contexts/AuthContext';
import Register from './components/Auth/Register';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import Terms from './components/Legal/Terms';
import Privacy from './components/Legal/Privacy';
import Onboarding from './components/Onboarding/Onboarding';
import NotFound from './components/NotFound';
import SettingsPage from './components/SettingsPage';
import AkahuCallback from './components/AkahuCallback';
import { usePreference } from './hooks/usePreference';
import TransactionCalendar from './components/UpcomingTransactions/UpcomingTransactions';

const AppContent = () => {
  // Router-level preferences: where the app opens and which analysis view is default
  const [defaultLandingPage] = usePreference('defaultLandingPage', '/dashboard');
  const [analysisDefaultPeriod] = usePreference('analysisDefaultPeriod', 'monthly');
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedTransactionForRule, setSelectedTransactionForRule] = React.useState(null);
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navigateToRuleCreation = (transaction) => {
    setSelectedTransactionForRule(transaction);
    navigate('/rules');
  };

  const menuItems = [
    { text: 'Overview', icon: <DashboardCustomizeIcon />, path: '/dashboard' },
    { text: 'Cashflow', icon: <InsightsIcon />, path: '/cashflow' },
    { text: 'Net Worth', icon: <AccountBalanceWalletOutlinedIcon />, path: '/networth' },
    { text: 'Transactions', icon: <ListAltIcon />, path: '/transactions' },
    { text: 'Calendar', icon: <CalendarMonthIcon />, path: '/calendar' },
    { text: 'Rules', icon: <RuleFolderIcon />, path: '/rules' },
    { text: 'Budgets', icon: <SavingsIcon />, path: '/budgets' },
    { text: 'Actions', icon: <AssignmentTurnedInIcon />, path: '/actions' },
    { text: 'Goals', icon: <FlagOutlinedIcon />, path: '/goals' },
    { text: 'Categories', icon: <CategoryOutlinedIcon />, path: '/categories' },
    { text: 'Accounts', icon: <AccountBalanceOutlinedIcon />, path: '/accounts' },
    { text: 'Import', icon: <CloudUploadIcon />, path: '/import' },
    { text: 'Settings', icon: <SettingsApplicationsIcon />, path: '/settings' },
  ];

  // Per-page browser tab titles ("Transactions · Budget Clues")
  React.useEffect(() => {
    const current = menuItems.find(item => location.pathname.startsWith(item.path));
    document.title = current ? `${current.text} · Budget Clues` : 'Budget Clues';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const drawer = (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, pb: 0.5 }}> {/* Reduced padding */}
        <BudgetCluesLogo size="medium" showText={true} mobile={isMobile} />
        {/* Close button for mobile drawer */}
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ color: 'white' }} aria-label="Close menu">
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }} />
      <List sx={{ flexGrow: 1 }}>
        {menuItems.map((item) => (
          <ListItem
            key={item.path}
            component={NavLink}
            to={item.path}
            onClick={isMobile ? handleDrawerToggle : undefined}
            sx={{
              py: 0.8, // Reduced from 1.5
              px: 1.5, // Reduced from 2
              color: 'white',
              cursor: 'pointer',
              borderRadius: 1,
              transition: 'background 0.2s, color 0.2s',
              '&.active': {
                backgroundColor: 'primary.main',
                color: 'white',
                '& .MuiListItemIcon-root': {
                  color: 'white',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.06)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'white', minWidth: 36 }}>{item.icon}</ListItemIcon> {/* Reduced from 40 */}
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{ variant: 'body2', sx: { fontSize: { xs: '1rem', md: '0.95rem' } } }}
            />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.12)' }} />
      <List>
        <ListItem
          button
          onClick={handleLogout}
          sx={{
            py: 0.8, // Reduced from 1.5
            px: 1.5, // Reduced from 2
            color: 'white',
            cursor: 'pointer',
            borderRadius: '6px', // Slightly reduced from 8px
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.06)',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'white', minWidth: 36 }}> {/* Reduced from implied 40 */}
            <LogoutIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ variant: 'body2', sx: { fontSize: { xs: '1rem', md: '0.95rem' } } }}
          />
        </ListItem>
      </List>
    </>
  );

  return (
    <LayoutProvider value={{ onMenuClick: handleDrawerToggle }}>
    <Box sx={{
      display: 'flex',
      overflow: 'hidden',
      maxWidth: '100vw',
      minHeight: '100vh'
    }}>
      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: 240, // Reduced from 270
            boxSizing: 'border-box',
            backgroundColor: '#1e1e1e',
            color: '#ffffff',
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Desktop drawer */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            width: 240, // Reduced from 270
            boxSizing: 'border-box',
            backgroundColor: '#1e1e1e',
            color: '#ffffff',
          },
        }}
        open
      >
        {drawer}
      </Drawer>

      {/* Main content - FIXED */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          pt: { xs: 0, md: 3 },
          // Narrow gutters on phones — 24px a side cost ~15% of a 390px
          // screen; the cards carry their own internal padding.
          pr: { xs: 1, sm: 2, md: 3 },
          pb: 3,
          pl: { xs: 1, sm: 2, md: 3 },
          ml: { md: '240px' }, // Updated from 270px
          overflow: 'hidden',
          maxWidth: '100%',
          boxSizing: 'border-box',
          minWidth: 0,
        }}
      >
        <Routes>
          <Route path="/" element={<Navigate to={defaultLandingPage} replace />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/dashboard" element={<MainDashboard />} />
          <Route path="/cashflow" element={<BudgetView viewType="monthly" />} />
          <Route path="/networth" element={<BudgetView viewType="yearly" />} />
          {/* Legacy Analysis URLs (bookmarks, stored landing-page preference) */}
          <Route path="/analysis" element={<Navigate to={analysisDefaultPeriod === 'yearly' ? '/networth' : '/cashflow'} replace />} />
          <Route path="/analysis/monthly" element={<Navigate to="/cashflow" replace />} />
          <Route path="/analysis/yearly" element={<Navigate to="/networth" replace />} />
          <Route path="/budgets" element={<Budgets />} />
          <Route path="/rules" element={<RulesManager transactionForRule={selectedTransactionForRule} clearTransactionForRule={() => setSelectedTransactionForRule(null)} />} />
          <Route path="/import" element={<CsvImportTool />} />
          <Route path="/transactions" element={<TransactionList onCreateRule={navigateToRuleCreation} />} />
          <Route path="/calendar" element={<TransactionCalendar />} />
          <Route path="/categories" element={<CategoryManager />} />
          <Route path="/accounts" element={<AccountManager />} />
          <Route path="/actions" element={<ActionManager />} />
          <Route path="/goals" element={<GoalList />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/auth/akahu/callback" element={<AkahuCallback />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Box>
    </Box>
    </LayoutProvider>
  );
};

function App() {
  return (
    <Router>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <PreferencesProvider>
            <DataProvider>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route
                  path="/*"
                  element={
                    <ProtectedRoute>
                      <AppContent />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            </DataProvider>
          </PreferencesProvider>
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;